import Template from "../../components/Template";
import React, { useState, useEffect, useRef } from "react";
import api from "../../services/api";
import AlertDialog from "../../components/AlertDialog";
import { formatCurrencyFromDb } from "../../utils/masks";
import usePermission from "../../hooks/usePermission";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from "xlsx";
import { useAuth } from '../../context/AuthContext';
import { formatDateWithoutHour } from "../../utils/date";
import useRequisicaoMaisRecente from '../../hooks/useRequisicaoMaisRecente';
import logoEmpresa from "../../components/logoEmpresaBase64"; // Importar o logo

const RelatorioGeralNotasFiscais = () => {
    const { auth } = useAuth();
    const [lista, setLista] = useState([]);
    const [impostos, setImpostos] = useState([]);
    const [columns, setColumns] = useState([]);
    const [menuOpen, setMenuOpen] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [message, setMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [incluirImpostos, setIncluirImpostos] = useState(true);

    // Estado para armazenar os OBJETOS COMPLETOS das linhas selecionadas
    const [selectedRows, setSelectedRows] = useState([]);

    const [filters, setFilters] = useState({
        dataInicial: "",
        dataFinal: ""
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const buttonRef = useRef(null);
    const buttonFiltrarRef = useRef(null);

    // Lógica para selecionar/deselecionar uma única linha
    const handleRowSelect = (row) => {
        setSelectedRows(prev => {
            const isSelected = prev.some(item => item.id === row.id && item.tipo === row.tipo);
            if (isSelected) {
                return prev.filter(item => !(item.id === row.id && item.tipo === row.tipo));
            } else {
                return [...prev, row];
            }
        });
    };

    // Lógica para o checkbox "Selecionar Todos" do cabeçalho
    const handleSelectAllOnPage = (e) => {
        if (e.target.checked) {
            // Adiciona todos os itens da página atual que ainda não estão na lista de seleção
            setSelectedRows(prev => {
                const newItems = lista.filter(item => !prev.some(sel => sel.id === item.id && sel.tipo === item.tipo));
                return [...prev, ...newItems];
            });
        } else {
            // Remove da seleção todos os itens que estão na página atual
            const pageIds = lista.map(item => `${item.id}-${item.tipo}`);
            setSelectedRows(prev => prev.filter(item => !pageIds.includes(`${item.id}-${item.tipo}`)));
        }
    };

    useEffect(() => {
        setColumns([
            {
                id: 'select',
                label: (
                    <input
                        type="checkbox"
                        title="Selecionar todos nesta página"
                        // O estado "checked" do checkbox principal depende se todos os itens visíveis já estão selecionados
                        checked={lista.length > 0 && lista.every(item => selectedRows.some(sel => sel.id === item.id && sel.tipo === item.tipo))}
                        onChange={handleSelectAllOnPage}
                    />
                ),
                visible: true,
                formatter: (value, row) => (
                    <input
                        type="checkbox"
                        checked={selectedRows.some(sel => sel.id === row.id && sel.tipo === row.tipo)}
                        onChange={() => handleRowSelect(row)}
                    />
                ),
            },
            { id: 'numero', label: 'Número NF', visible: true, formatter: (value) => value || "N/A" },
            { id: 'fornecedor', label: 'Fornecedor', visible: true, formatter: (value) => value.nomeFornecedor || value.razaoSocial || "N/A" },
            { id: 'dataEmissao', label: 'Data Emissão', visible: true, formatter: (value) => (value ? formatDateWithoutHour(value) : "") },
            { id: 'valorTotal', label: 'Valor Total', visible: true, formatter: (value) => formatCurrencyFromDb(value) },
            { id: 'obra', label: 'Obra', visible: true, formatter: (obra) => obra?.nomeProjeto?.toUpperCase() || 'Sem Obra Vinculada' },
            { id: 'tipo', label: 'Tipo', visible: true, formatter: (value) => value },
        ]);
    }, [lista, selectedRows]); // Depende de `selectedRows` para re-renderizar o header checkbox

    // Descarta respostas de filtros que já foram superados por outro mais novo:
    // sem isto, a resposta lenta de um filtro anterior sobrescrevia a tela.
    const requisicaoMaisRecente = useRequisicaoMaisRecente();

    const fetchNotasFiscais = async (page, size, currentFilters) => {
        if (!currentFilters.dataInicial || !currentFilters.dataFinal) {
            setMessage("Por favor, selecione a data inicial e final para filtrar.");
            setShowSuccessDialog(true);
            return;
        }
        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                dataInicial: currentFilters.dataInicial,
                dataFinal: currentFilters.dataFinal,
                page: page,
                pageSize: size,
                incluirImpostos: incluirImpostos ? 'true' : 'false',
            });

            const idRequisicao = requisicaoMaisRecente.iniciar();
            const response = await api.get(`/relatorios/notasfiscais/todas?${params.toString()}`);

            // Chegou atrasada e ja foi superada por outro filtro: descarta.
            if (!requisicaoMaisRecente.ehAtual(idRequisicao)) return;


            setLista(response.data.records || []);
            setImpostos(response.data.impostos || []);
            setTotalRecords(response.data.total || 0);
            setTotalPages(response.data.totalPages || 0);
            setCurrentPage(response.data.page || 1);

        } catch (error) {
            console.error("Erro ao buscar notas fiscais:", error);
            setMessage(error.response?.data?.error || "Erro ao buscar notas fiscais.");
            setShowSuccessDialog(true);
            setLista([]);
            setTotalRecords(0);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (filters.dataInicial && filters.dataFinal) {
            fetchNotasFiscais(currentPage, pageSize, filters);
        }
    }, [currentPage]);

    const handlePageSizeChange = (newPageSize) => {
        const firstRecordOnPage = (currentPage - 1) * pageSize + 1;
        const newCurrentPage = Math.ceil(firstRecordOnPage / newPageSize);

        setPageSize(newPageSize);
        const newTotalPages = Math.ceil(totalRecords / newPageSize);
        const targetPage = Math.min(newCurrentPage, newTotalPages) || 1;
        setCurrentPage(targetPage);

        if (filters.dataInicial && filters.dataFinal) {
            fetchNotasFiscais(targetPage, newPageSize, filters);
        }
    };

    const applyFilters = () => {
        setCurrentPage(1);
        setSelectedRows([]);
        fetchNotasFiscais(1, pageSize, filters);
        setMenuOpen(null);
    };

    const clearFilters = () => {
        setFilters({ dataInicial: "", dataFinal: "" });
        setLista([]);
        setImpostos([]);
        setTotalRecords(0);
        setTotalPages(0);
        setCurrentPage(1);
        setSelectedRows([]);
    };

    const handlePageChange = (page) => {
        if (page > 0 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const toggleColumnVisibility = (id) => {
        if (id === 'select') return; // Impede que a coluna de seleção seja desabilitada
        setColumns((prev) => prev.map((col) => col.id === id ? { ...col, visible: !col.visible } : col));
    };

    const exportarDados = async (exportFunction) => {
        const hasSelection = selectedRows.length > 0;

        if (!hasSelection && (!totalRecords || totalRecords === 0) && impostos.length === 0) {
            setMessage('Não há dados para exportar!');
            setShowSuccessDialog(true);
            return;
        }

        let dataToExport = [];
        let impostosToExport = incluirImpostos ? impostos : [];

        if (hasSelection) {
            dataToExport = selectedRows;
            exportFunction(dataToExport, impostosToExport);
        } else {
            setMessage('Preparando todos os dados para exportação... Isso pode levar um momento.');
            setShowSuccessDialog(true);
            try {
                const params = new URLSearchParams({
                    dataInicial: filters.dataInicial,
                    dataFinal: filters.dataFinal,
                    page: 1,
                    pageSize: totalRecords || 1,
                    incluirImpostos: incluirImpostos ? 'true' : 'false',
                });
                const response = await api.get(`/relatorios/notasfiscais/todas?${params.toString()}`);
                dataToExport = response.data.records;
                impostosToExport = response.data.impostos || [];
                exportFunction(dataToExport, impostosToExport);
            } catch (error) {
                console.error("Erro ao exportar dados:", error);
                setMessage("Falha ao preparar dados para exportação.");
            }
        }
    };

    const exportarPDF = (data, impostosData = []) => {
        const doc = new jsPDF({ orientation: "landscape" });
        doc.setFontSize(16);
        doc.text("Relatório Geral de Notas Fiscais", 14, 20);
        doc.setFontSize(10);
        doc.text(`Período: ${formatDateWithoutHour(filters.dataInicial)} a ${formatDateWithoutHour(filters.dataFinal)}`, 14, 28);
        if (selectedRows.length > 0) {
            doc.text(`Exportando ${selectedRows.length} linha(s) selecionada(s).`, 14, 34);
        }

        const visibleColumns = columns.filter(c => c.visible && c.id !== 'select');
        const head = [visibleColumns.map(c => c.label)];
        const body = data.map(row => visibleColumns.map(col => col.formatter(row[col.id], row)));

        const totalValor = data.reduce((sum, item) => sum + (Number(item.valorTotal) || 0), 0);
        const totalFormatado = formatCurrencyFromDb(totalValor);

        const totalRow = Array(head[0].length).fill('');
        const valorTotalIndex = visibleColumns.findIndex(c => c.id === 'valorTotal');
        if (valorTotalIndex !== -1) {
            totalRow[valorTotalIndex - 1] = { content: 'TOTAL:', styles: { fontStyle: 'bold', halign: 'right' } };
            totalRow[valorTotalIndex] = { content: totalFormatado, styles: { fontStyle: 'bold' } };
        }
        body.push(totalRow);

        autoTable(doc, {
            startY: 40,
            head,
            body,
            headStyles: { fillColor: [30, 120, 180] },
            bodyStyles: { valign: 'middle' },
            didDrawCell: (data) => {
                if (data.row.index === body.length - 1) { doc.setFont(undefined, 'bold'); }
            }
        });

        // Adicionar seção de impostos se houver
        if (impostosData.length > 0) {
            const finalY = doc.lastAutoTable.finalY + 15;
            
            doc.setFontSize(14);
            doc.setTextColor(0, 0, 0);
            doc.text("Impostos do Período", 14, finalY);
            
            const impostosHead = [['Tipo', 'Descrição', 'Valor', 'Vencimento', 'Pagamento', 'Status', 'Referência']];
            const impostosBody = impostosData.map(imp => [
                imp.tipo?.nome || '-',
                imp.descricao || '-',
                formatCurrencyFromDb(imp.valor),
                imp.dataVencimento ? formatDateWithoutHour(imp.dataVencimento) : '-',
                imp.dataPagamento ? formatDateWithoutHour(imp.dataPagamento) : '-',
                imp.status?.nome || '-',
                imp.referencia || '-'
            ]);

            const totalImpostos = impostosData.reduce((sum, item) => sum + (Number(item.valor) || 0), 0);
            const totalImpostosFormatado = formatCurrencyFromDb(totalImpostos);
            impostosBody.push(['', { content: 'TOTAL:', styles: { fontStyle: 'bold', halign: 'right' } }, { content: totalImpostosFormatado, styles: { fontStyle: 'bold' } }, '', '', '', '']);

            autoTable(doc, {
                startY: finalY + 5,
                head: impostosHead,
                body: impostosBody,
                headStyles: { fillColor: [120, 80, 40] },
                bodyStyles: { valign: 'middle' },
            });
        }

        const pageCount = doc.internal.getNumberOfPages();
        const dataEmissao = new Date().toLocaleString('pt-BR');
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(100);
            doc.text(`Emitido por: ${auth.user.name || "Sistema"}`, 14, doc.internal.pageSize.height - 15);
            doc.text(`Data/Hora: ${dataEmissao}`, 14, doc.internal.pageSize.height - 10);
            doc.text(`Página ${i} de ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10, { align: 'right' });
            if (logoEmpresa) {
                const logoWidth = 40, logoHeight = 12, pageWidth = doc.internal.pageSize.getWidth();
                doc.addImage(logoEmpresa, 'PNG', (pageWidth - logoWidth) / 2, doc.internal.pageSize.height - 18, logoWidth, logoHeight);
            }
        }

        doc.save("relatorio_geral_nf.pdf");
    };

    const exportarExcel = (data, impostosData = []) => {
        const reportHeaders = [["Relatório Geral de Notas Fiscais"], [`Período: ${formatDateWithoutHour(filters.dataInicial)} a ${formatDateWithoutHour(filters.dataFinal)}`], []];
        if (selectedRows.length > 0) {
            reportHeaders.splice(2, 0, [`Exportando ${selectedRows.length} linha(s) selecionada(s).`]);
        }

        const visibleColumns = columns.filter(c => c.visible && c.id !== 'select');
        const tableHeaders = visibleColumns.map(c => c.label);

        const tableRows = data.map(row => visibleColumns.map(col => {
            if (col.id === 'valorTotal') return parseFloat(row.valorTotal || 0);
            return col.formatter(row[col.id], row);
        }));

        const totalValor = data.reduce((sum, item) => sum + (Number(item.valorTotal) || 0), 0);
        const totalRow = Array(tableHeaders.length).fill('');
        const valorTotalIndex = visibleColumns.findIndex(c => c.id === 'valorTotal');
        if (valorTotalIndex !== -1) {
            totalRow[valorTotalIndex - 1] = "TOTAL:";
            totalRow[valorTotalIndex] = totalValor;
        }

        // Construir array com notas fiscais
        let allData = [...reportHeaders, tableHeaders, ...tableRows, [], totalRow];

        // Guardar linha inicial dos impostos para formatação
        let impostosStartRow = allData.length;

        // Adicionar seção de impostos se houver
        if (impostosData.length > 0) {
            allData.push([], [], ["Impostos do Período"], []);
            impostosStartRow = allData.length + 1; // Linha após o cabeçalho dos impostos
            const impostosHeaders = ['Tipo', 'Descrição', 'Valor', 'Vencimento', 'Pagamento', 'Status', 'Referência'];
            allData.push(impostosHeaders);
            
            impostosData.forEach(imp => {
                allData.push([
                    imp.tipo?.nome || '-',
                    imp.descricao || '-',
                    parseFloat(imp.valor) || 0,
                    imp.dataVencimento ? formatDateWithoutHour(imp.dataVencimento) : '-',
                    imp.dataPagamento ? formatDateWithoutHour(imp.dataPagamento) : '-',
                    imp.status?.nome || '-',
                    imp.referencia || '-'
                ]);
            });

            const totalImpostos = impostosData.reduce((sum, item) => sum + (Number(item.valor) || 0), 0);
            allData.push(['', 'TOTAL:', totalImpostos, '', '', '', '']);
        }

        const worksheet = XLSX.utils.aoa_to_sheet(allData);

        // Formatar valores de notas fiscais
        if (valorTotalIndex > -1) {
            const letter = XLSX.utils.encode_col(valorTotalIndex);
            const startRow = reportHeaders.length + 2;
            for (let i = startRow; i <= tableRows.length + (startRow - 1); i++) {
                const cell = worksheet[`${letter}${i}`];
                if (cell) cell.z = 'R$ #,##0.00';
            }
            const totalCell = worksheet[`${letter}${tableRows.length + startRow + 1}`];
            if (totalCell) totalCell.z = 'R$ #,##0.00';
        }

        // Formatar valores de impostos (coluna C = índice 2)
        if (impostosData.length > 0) {
            const impostosValueCol = 'C'; // Coluna de Valor nos impostos
            // Linha do cabeçalho dos impostos + dados + total
            const impostosHeaderRowNum = reportHeaders.length + tableRows.length + 7; // Ajuste baseado na estrutura
            
            // Calcular a linha correta onde começam os dados dos impostos
            // reportHeaders (3-4 linhas) + tableHeaders (1) + tableRows + linha vazia + totalRow + 3 linhas vazias + titulo + linha vazia + impostosHeaders
            const baseRow = reportHeaders.length + 1 + tableRows.length + 1 + 1 + 3 + 1 + 1 + 1;
            
            // Formatar cada linha de imposto
            for (let i = 0; i < impostosData.length; i++) {
                const rowNum = baseRow + i;
                const cell = worksheet[`${impostosValueCol}${rowNum}`];
                if (cell) cell.z = 'R$ #,##0.00';
            }
            
            // Formatar linha de total dos impostos
            const totalImpostosRowNum = baseRow + impostosData.length;
            const totalImpostosCell = worksheet[`${impostosValueCol}${totalImpostosRowNum}`];
            if (totalImpostosCell) totalImpostosCell.z = 'R$ #,##0.00';
        }

        worksheet['!cols'] = tableHeaders.map(h => ({ wch: h.length + 10 }));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "RelatorioGeralNF");
        XLSX.writeFile(workbook, "relatorio_geral_nf.xlsx");
    };

    const handleClickOutside = (event) => {
        if (buttonRef.current && !buttonRef.current.contains(event.target) && buttonFiltrarRef.current && !buttonFiltrarRef.current.contains(event.target)) {
            setMenuOpen(null);
        }
    };
    useEffect(() => { document.addEventListener("mousedown", handleClickOutside); return () => document.removeEventListener("mousedown", handleClickOutside); }, []);

    const pageNumbers = Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
        let page = currentPage - 2 + i;
        if (page < 1) page = 1 + i;
        if (currentPage <= 2) page = i + 1;
        if (currentPage >= totalPages - 1 && totalPages > 4) page = totalPages - 4 + i;
        return page;
    }).filter((p, i, self) => p > 0 && p <= totalPages && self.indexOf(p) === i);

    return (
        <Template>
            <div className="h-auto w-full p-4 md:p-8 flex flex-col gap-6">
                <div className="text-stone-800 text-xl md:text-2xl font-medium">Relatório Geral de Notas Fiscais</div>
                <div className="flex flex-wrap gap-2 md:gap-4 items-center">
                    <div className="flex-grow min-w-0 flex items-center gap-2">
                        <div className="text-stone-600 text-sm font-medium">Ver</div>
                        <select className="w-20 h-8 pl-2 pr-2 py-1 bg-white rounded-md border border-slate-300 text-sm" value={pageSize} onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                            <option value={500}>500</option>
                            <option value={1000}>1000</option>
                        </select>
                        <div className="text-stone-600 text-sm font-medium">Resultados</div>
                    </div>
                    <div className="flex flex-wrap gap-2 flex-none w-full md:w-auto">
                        <div ref={buttonRef} className="relative justify-end items-center gap-1 flex cursor-pointer" onClick={() => setMenuOpen(menuOpen === "colunas" ? null : "colunas")}>
                            <div className="justify-start items-center gap-2 flex"><img className="w-6 h-6" src="/images/icon/columns.png" /><div className="text-stone-600 text-sm font-medium">Colunas</div></div><img className="w-6 h-4 px-1" src="/images/icon/chevron-down.png" />
                            {menuOpen === "colunas" && (<div className="absolute top-full right-0 mt-2 w-48 bg-white border rounded-md shadow-lg z-50 p-4 flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>{columns.map((col) => (<div key={col.id} className="flex items-center gap-2"><input type="checkbox" id={col.id} checked={col.visible} disabled={col.id === 'select'} onChange={() => toggleColumnVisibility(col.id)} /><label htmlFor={col.id} className="text-sm text-gray-700">{col.label}</label></div>))}</div>)}
                        </div>
                        <div ref={buttonFiltrarRef} className="relative justify-start items-center gap-1 flex cursor-pointer" onClick={() => setMenuOpen(menuOpen === "filtro" ? null : "filtro")}>
                            <div className="justify-start items-center gap-2 flex"><img className="w-8 h-8" src="/images/icon/filter.png" /><div className="text-stone-600 text-sm font-medium">Filtrar</div></div><img className="w-6 h-4 px-1" src="/images/icon/chevron-down.png" />
                            {menuOpen === "filtro" && (
                                <div className="absolute top-full right-0 mt-2 w-[350px] bg-white rounded-md shadow-lg z-50 p-4 border" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex flex-col gap-4">
                                        <div className="text-stone-800 text-xl font-medium">Filtrar por Período</div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="flex flex-col gap-1.5"><label className="text-slate-700 text-sm font-medium">Data Inicial</label><input type="date" className="w-full px-3 py-2 bg-white rounded-md border" value={filters.dataInicial} onChange={(e) => setFilters({ ...filters, dataInicial: e.target.value })} /></div>
                                            <div className="flex flex-col gap-1.5"><label className="text-slate-700 text-sm font-medium">Data Final</label><input type="date" className="w-full px-3 py-2 bg-white rounded-md border" value={filters.dataFinal} onChange={(e) => setFilters({ ...filters, dataFinal: e.target.value })} /></div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <input type="checkbox" id="incluirImpostos" checked={incluirImpostos} onChange={(e) => setIncluirImpostos(e.target.checked)} className="w-4 h-4" />
                                            <label htmlFor="incluirImpostos" className="text-slate-700 text-sm font-medium cursor-pointer">Incluir Impostos do Período</label>
                                        </div>
                                        <div className="flex items-center justify-between gap-4 mt-4">
                                            <button className="px-4 py-2 w-full bg-white rounded-md border border-stone-500 flex items-center justify-center gap-2" onClick={clearFilters}><img className="w-4 h-4" src="/images/icon/eraser.png" /><div className="text-stone-500 text-sm">Limpar</div></button>
                                            <button className="px-4 py-2 w-full bg-stone-600 rounded-md flex items-center justify-center gap-2" onClick={applyFilters}><img className="w-4 h-4" src="/images/icon/filter-white.png" /><div className="text-white text-sm">Filtrar</div></button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="flex gap-4">
                            {usePermission('EXPORTAR_RELATORIOS_GERAL_NOTAS') && (<>
                                <button type='button' className="px-4 py-2 bg-green-600 text-white rounded-md flex items-center gap-2" onClick={() => exportarDados(exportarExcel)}><img className="w-4 h-4" src="/images/icon/upload.png" /><span>Excel</span></button>
                                <button type='button' className="px-4 py-2 bg-slate-600 text-white rounded-md flex items-center gap-2" onClick={() => exportarDados(exportarPDF)}><img className="w-4 h-4" src="/images/icon/pdf.png" /><span>PDF</span></button>
                            </>)}
                        </div>
                    </div>
                </div>

                <div className="flex justify-between items-center px-1">
                    <div className="text-sm text-gray-600">
                        {selectedRows.length > 0 && `${selectedRows.length} linha(s) selecionada(s).`}
                    </div>
                    {selectedRows.length > 0 && (
                        <button onClick={() => setSelectedRows([])} className="text-sm text-blue-600 hover:text-blue-800 font-semibold">
                            Limpar seleção
                        </button>
                    )}
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border-collapse border border-gray-200">
                        <thead className="bg-stone-200">
                            <tr>{columns.filter(col => col.visible).map(col => (<th key={col.id} className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold">{col.label}</th>))}</tr>
                        </thead>
                        <tbody>
                            {isLoading ? (<tr><td colSpan={columns.filter(c => c.visible).length} className="text-center py-10 text-slate-500">Carregando...</td></tr>
                            ) : lista.length > 0 ? (
                                lista.map((doc) => (
                                    <tr key={`${doc.id}-${doc.tipo}`} className={`hover:bg-gray-100 ${selectedRows.some(sel => sel.id === doc.id && sel.tipo === doc.tipo) ? 'bg-blue-100' : ''}`}>
                                        {columns.filter(col => col.visible).map(col => (<td key={`${col.id}-${doc.id}-${doc.tipo}`} className="px-4 py-2 border border-slate-200 text-slate-700 text-base">{col.formatter(doc[col.id], doc)}</td>))}
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan={columns.filter(c => c.visible).length} className="text-center py-10 text-slate-500">Nenhum registro encontrado. Utilize o filtro para iniciar a busca.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalRecords > 0 && !isLoading && (
                    <div className="self-stretch justify-end items-center gap-2.5 inline-flex">
                        <div className="grow shrink basis-0 text-slate-500 text-base">Mostrando de {(currentPage - 1) * pageSize + 1} até {Math.min(currentPage * pageSize, totalRecords)} de {totalRecords} registros. (Página {currentPage} de {totalPages})</div>
                        <div className="justify-start items-start gap-4 flex">
                            <button className={`w-10 h-10 rounded border border-gray-300 disabled:opacity-50 flex justify-center items-center`} disabled={currentPage === 1} onClick={() => handlePageChange(1)} title="Primeira página"><img className="w-6 h-6" src="/images/icon/keyboard-arrow-left.png" style={{ transform: 'scaleX(1.5)' }} /></button>
                            <button className={`w-10 h-10 rounded border border-gray-300 disabled:opacity-50 flex justify-center items-center`} disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} title="Página anterior"><img className="w-6 h-6" src="/images/icon/keyboard-arrow-left.png" /></button>
                            {pageNumbers.map(page => (<button key={page} className={`w-10 h-10 rounded flex justify-center items-center font-semibold ${page === currentPage ? "bg-stone-600 text-white" : "bg-gray-200 text-gray-600"}`} onClick={() => handlePageChange(page)}>{page}</button>))}
                            <button className={`w-10 h-10 rounded border border-gray-300 disabled:opacity-50 flex justify-center items-center`} disabled={currentPage >= totalPages} onClick={() => handlePageChange(currentPage + 1)} title="Próxima página"><img className="w-6 h-6" src="/images/icon/keyboard-arrow-right.png" /></button>
                            <button className={`w-10 h-10 rounded border border-gray-300 disabled:opacity-50 flex justify-center items-center`} disabled={currentPage >= totalPages} onClick={() => handlePageChange(totalPages)} title="Última página"><img className="w-6 h-6" src="/images/icon/keyboard-arrow-right.png" style={{ transform: 'scaleX(1.5)' }} /></button>
                        </div>
                    </div>
                )}

                {/* Seção de Impostos */}
                {incluirImpostos && impostos.length > 0 && !isLoading && (
                    <div className="mt-6">
                        <div className="text-stone-800 text-lg md:text-xl font-medium mb-4">Impostos do Período</div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border-collapse border border-gray-200">
                                <thead className="bg-amber-700">
                                    <tr>
                                        <th className="px-4 py-2 border border-slate-200 text-white text-base font-bold">Tipo</th>
                                        <th className="px-4 py-2 border border-slate-200 text-white text-base font-bold">Descrição</th>
                                        <th className="px-4 py-2 border border-slate-200 text-white text-base font-bold">Valor</th>
                                        <th className="px-4 py-2 border border-slate-200 text-white text-base font-bold">Vencimento</th>
                                        <th className="px-4 py-2 border border-slate-200 text-white text-base font-bold">Pagamento</th>
                                        <th className="px-4 py-2 border border-slate-200 text-white text-base font-bold">Status</th>
                                        <th className="px-4 py-2 border border-slate-200 text-white text-base font-bold">Referência</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {impostos.map((imposto) => (
                                        <tr key={imposto.id} className="hover:bg-gray-100">
                                            <td className="px-4 py-2 border border-slate-200 text-slate-700 text-base">{imposto.tipo?.nome || '-'}</td>
                                            <td className="px-4 py-2 border border-slate-200 text-slate-700 text-base">{imposto.descricao || '-'}</td>
                                            <td className="px-4 py-2 border border-slate-200 text-slate-700 text-base">{formatCurrencyFromDb(imposto.valor)}</td>
                                            <td className="px-4 py-2 border border-slate-200 text-slate-700 text-base">{imposto.dataVencimento ? formatDateWithoutHour(imposto.dataVencimento) : '-'}</td>
                                            <td className="px-4 py-2 border border-slate-200 text-slate-700 text-base">{imposto.dataPagamento ? formatDateWithoutHour(imposto.dataPagamento) : '-'}</td>
                                            <td className="px-4 py-2 border border-slate-200 text-slate-700 text-base">{imposto.status?.nome || '-'}</td>
                                            <td className="px-4 py-2 border border-slate-200 text-slate-700 text-base">{imposto.referencia || '-'}</td>
                                        </tr>
                                    ))}
                                    <tr className="bg-gray-100 font-bold">
                                        <td className="px-4 py-2 border border-slate-200 text-slate-700 text-base" colSpan={2} style={{ textAlign: 'right' }}>TOTAL:</td>
                                        <td className="px-4 py-2 border border-slate-200 text-slate-700 text-base">{formatCurrencyFromDb(impostos.reduce((sum, imp) => sum + (Number(imp.valor) || 0), 0))}</td>
                                        <td className="px-4 py-2 border border-slate-200 text-slate-700 text-base" colSpan={4}></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="text-slate-500 text-sm mt-2">Total de {impostos.length} imposto(s) encontrado(s) no período.</div>
                    </div>
                )}

                <AlertDialog isVisible={showSuccessDialog} message={message} onClose={() => setShowSuccessDialog(false)} />
            </div>
        </Template>
    );
};

export default RelatorioGeralNotasFiscais;